import { HEIC_MIME_TYPES, TIFF_MIME_TYPES } from "@lib/constants/mime"
import { decodeImageToDisplayableSrc } from "@lib/media/decode-image"
import { useEffect, useMemo, useRef, useState } from "react"

type UseDisplayableUploadSrcInput = {
  /** Remote/original asset URL (used as fallback and for non-HEIC/TIFF types). */
  url: string | null | undefined
  mimetype: string | null | undefined
  /** If true, uses imgix-style remote URL already; if false will just use url as-is. */
  remoteUrlTransformer?: (url: string) => string
}

type UseDisplayableUploadSrcResult = {
  src: string | null
  isDecoded: boolean
}

export const needsDecode = (mime?: string | null) => {
  if (!mime) return false
  return HEIC_MIME_TYPES.has(mime) || TIFF_MIME_TYPES.has(mime)
}

/**
 * Returns an <img>-renderable src for an upload.
 * - For typical images: returns remote URL (optionally transformed)
 * - For HEIC/TIFF: fetches from remote URL, decodes client-side and returns a blob/data URL
 */
export function useDisplayableUploadSrc({
  url,
  mimetype,
  remoteUrlTransformer,
}: UseDisplayableUploadSrcInput): UseDisplayableUploadSrcResult {
  const remoteSrc = useMemo(() => {
    if (!url) return null
    return remoteUrlTransformer ? remoteUrlTransformer(url) : url
  }, [url, remoteUrlTransformer])

  const shouldDecode = useMemo(() => needsDecode(mimetype), [mimetype])

  const [decodedSrc, setDecodedSrc] = useState<string | null>(null)
  const revokeRef = useRef<null | (() => void)>(null)

  useEffect(() => {
    let cancelled = false
    const abortController = new AbortController()

    // cleanup previous decoded src
    setDecodedSrc(null)
    if (revokeRef.current) {
      revokeRef.current()
      revokeRef.current = null
    }

    if (!shouldDecode || !remoteSrc) {
      return () => {
        cancelled = true
        abortController.abort()
      }
    }

    async function fetchAndDecodeImage() {
      if (!remoteSrc) return

      try {
        const response = await fetch(remoteSrc, {
          signal: abortController.signal,
        })
        if (!response.ok) {
          throw new Error(
            `Failed to fetch image for decoding: ${response.status}`,
          )
        }

        const blob = await response.blob()
        if (cancelled || abortController.signal.aborted) return

        const syntheticFile = new File([blob], "remote-upload", {
          type: blob.type || mimetype || undefined,
        })

        const res = await decodeImageToDisplayableSrc({
          file: syntheticFile,
          fallbackMimeType: mimetype || undefined,
        })
        if (cancelled || abortController.signal.aborted) {
          if (res.kind === "blob-url") res.revoke()
          return
        }

        if (res.kind === "blob-url") {
          setDecodedSrc(res.blobUrl)
          revokeRef.current = res.revoke
        } else {
          setDecodedSrc(res.dataUrl)
          revokeRef.current = null
        }
      } catch {
        // fall back to remoteSrc if fetch or decode fails (CORS, network, etc.)
        setDecodedSrc(null)
        revokeRef.current = null
      }
    }

    fetchAndDecodeImage()

    return () => {
      cancelled = true
      if (revokeRef.current) {
        revokeRef.current()
        revokeRef.current = null
      }
      abortController.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // Excluding `revokeRef` from deps: refs are stable and don't need to be in the dependency array.
    // The ref is only used to store/access the revoke function, not to trigger re-runs of the effect.
  }, [remoteSrc, shouldDecode, mimetype])

  return {
    src: decodedSrc || remoteSrc,
    isDecoded: !!decodedSrc,
  }
}
